import { getAdminToken } from "../api/adminClient";
import { joinPath } from "../api/client";

const V1_API_BASE = (process.env.REACT_APP_V1_API_URL || "").replace(/\/?$/, "");
export const MAX_LOGO_BYTES = 5 * 1024 * 1024;
/** Extensions the backend S3 upload accepts for images. */
export const LOGO_ALLOWED_EXTENSIONS = ["png", "jpg", "jpeg", "gif"];
export const LOGO_ACCEPT =
  ".png,.jpg,.jpeg,.gif,image/png,image/jpeg,image/gif";

function fileExtension(file) {
  const name = String(file?.name || "");
  const i = name.lastIndexOf(".");
  if (i < 0 || i === name.length - 1) return "";
  return name.slice(i + 1).toLowerCase();
}

function readAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () =>
      reject(new Error("Could not read that image file. Try another PNG or JPG."));
    reader.readAsDataURL(file);
  });
}

/**
 * Map API / network errors to a short reason the admin can act on.
 */
export function formatLogoUploadError(err, { fallback = "Logo upload failed" } = {}) {
  if (!err) return fallback;
  const code = Number(err.code || err.status || 0);
  const raw = String(err.message || err.statusText || "").trim();
  const lower = raw.toLowerCase();

  if (err.name === "TypeError" || /failed to fetch|networkerror|load failed|network/i.test(raw)) {
    return "Could not reach the server. Check your connection and try again.";
  }
  if (code === 401 || /unauthorized|not authenticated|login|token/i.test(lower)) {
    return "Your session expired. Sign in again, then retry the logo upload.";
  }
  if (code === 403 || /forbidden|permission|not allowed|companies:write/i.test(lower)) {
    return "You do not have permission to upload logos in this workspace.";
  }
  if (
    code === 503 ||
    err.s3NotConfigured ||
    /not configured|access_key|secret_access|bucket_name|s3/i.test(lower)
  ) {
    return "File storage is not configured on this server. Ask an admin to set S3 credentials, or paste a public image URL instead.";
  }
  if (code === 413 || /too large|exceeds the allowed|5 mb|5mb/i.test(lower)) {
    return "That image is too large. Use a file under 5 MB.";
  }
  if (
    /format not supported|file type|not an image|unsupported|extension/i.test(lower)
  ) {
    return "That file type is not supported. Use PNG, JPG, or GIF.";
  }
  if (code === 400 && /no files/i.test(lower)) {
    return "No image file was selected. Choose a PNG, JPG, or GIF and try again.";
  }
  if (code >= 500 || /failed to upload file to s3|internal server/i.test(lower)) {
    return "Upload service error. Try again in a moment, or paste a public image URL instead.";
  }
  if (raw && !/^logo upload failed$/i.test(raw)) {
    // Prefer specific server message when it is already clear.
    if (raw.length <= 180) return raw;
    return `${raw.slice(0, 177)}…`;
  }
  return fallback;
}

function validateLogoFile(file) {
  if (!file) {
    const err = new Error("No image file was selected.");
    err.code = 400;
    throw err;
  }
  if (file.size > MAX_LOGO_BYTES) {
    const err = new Error("That image is too large. Use a file under 5 MB.");
    err.code = 413;
    throw err;
  }
  const ext = fileExtension(file);
  const typeOk =
    /^image\/(png|jpe?g|gif)$/i.test(file.type || "") ||
    LOGO_ALLOWED_EXTENSIONS.includes(ext);
  if (!typeOk) {
    const err = new Error(
      ext
        ? `“.${ext}” files are not supported. Use PNG, JPG, or GIF.`
        : "That file type is not supported. Use PNG, JPG, or GIF."
    );
    err.code = 400;
    throw err;
  }
}

async function uploadViaV2Api(file) {
  const formData = new FormData();
  formData.append("files", file);
  const token = getAdminToken();
  const headers = {};
  if (token) headers["x-access-token"] = token;

  let res;
  try {
    res = await fetch(joinPath("v2/engagements/upload"), {
      method: "POST",
      headers,
      body: formData,
    });
  } catch (networkErr) {
    const err = new Error("Could not reach the server. Check your connection and try again.");
    err.code = 0;
    err.cause = networkErr;
    throw err;
  }

  const data = await res.json().catch(() => ({}));
  const url = data?.data?.urls?.[0];
  if (res.ok && url) return url;

  const err = new Error(data?.message || "Logo upload failed");
  err.code = data?.code ?? res.status;
  err.s3NotConfigured =
    res.status === 503 || /not configured/i.test(String(data?.message || ""));
  throw err;
}

async function uploadViaV1Api(file) {
  const formData = new FormData();
  formData.append("files", file);
  let res;
  try {
    res = await fetch(`${V1_API_BASE}/POSTfileupload`, {
      method: "POST",
      body: formData,
    });
  } catch (networkErr) {
    const err = new Error("Could not reach the upload server. Check your connection and try again.");
    err.code = 0;
    err.cause = networkErr;
    throw err;
  }
  const data = await res.json().catch(() => ({}));
  const url = data?.data?.urls?.[0];
  if (res.ok && url) return url;
  const err = new Error(data?.message || "Logo upload failed");
  err.code = data?.code ?? res.status;
  throw err;
}

/**
 * Upload company / workspace logo to S3 (v2 endpoint).
 * Falls back to v1 when configured; uses inline data URL only when S3 is not configured.
 */
export async function uploadCompanyLogo(file) {
  validateLogoFile(file);

  try {
    return await uploadViaV2Api(file);
  } catch (v2Err) {
    // Validation / auth errors should not be masked by a secondary upload attempt.
    const code = Number(v2Err.code || 0);
    if (code === 400 || code === 401 || code === 403 || code === 413) {
      v2Err.message = formatLogoUploadError(v2Err);
      throw v2Err;
    }

    if (v2Err.s3NotConfigured) {
      try {
        return await readAsDataUrl(file);
      } catch (readErr) {
        readErr.message = formatLogoUploadError(readErr);
        throw readErr;
      }
    }

    if (V1_API_BASE && (code === 0 || code >= 500 || code === 503)) {
      try {
        return await uploadViaV1Api(file);
      } catch (v1Err) {
        v1Err.message = formatLogoUploadError(v1Err, {
          fallback: formatLogoUploadError(v2Err),
        });
        throw v1Err;
      }
    }

    v2Err.message = formatLogoUploadError(v2Err);
    throw v2Err;
  }
}
